/**
 * Owner: Masa Bayu
 * File: CommerceCheckoutPanel.tsx
 * Date:Time: 2026-09-15 22:25
 * Description: T3-E1 checkout totals UI — contracts visible; no payment execute.
 */

import { useState } from 'react';
import { QXREAD_COMMERCE_RATE_CONTRACT } from '@qx/qxread-core';
import { formatMarketplacePriceOrUnknown } from '../home/marketplace/marketplace-locale';
import { pushLandingAlert } from '../home/marketplace/landing/landing-alerts';
import {
  createDemoPendingOrder,
  getDemoCart,
  getDemoOrder,
  prepareDemoCheckout,
} from './demo-cart-store';

export interface CommerceCheckoutPanelProps {
  readonly onNavigate: (path: string) => void;
}

/** §466 S1 — jumlah checkout ikut mata wang SSOT ledger + koma ribuan. */
function formatTotal(minor: number, currency: string): string {
  return formatMarketplacePriceOrUnknown(minor, currency, `Price unavailable (${currency})`);
}

export function CommerceCheckoutPanel({ onNavigate }: CommerceCheckoutPanelProps) {
  const [revision, setRevision] = useState(0);
  const checkout = prepareDemoCheckout();
  const order = getDemoOrder();
  const cart = getDemoCart();
  const totals = checkout.totals;
  const feePct = (QXREAD_COMMERCE_RATE_CONTRACT.serviceFeeBuyerPaidBps / 100).toFixed(1);
  const affPct = (QXREAD_COMMERCE_RATE_CONTRACT.affiliateAutomaticBps / 100).toFixed(0);
  const refPct = (QXREAD_COMMERCE_RATE_CONTRACT.referralAutomaticBps / 100).toFixed(0);
  const revisionLabel = `rev-${String(revision)}`;

  return (
    <section
      className="route-panel"
      aria-labelledby="route-title"
      data-checkout-revision={revisionLabel}
    >
      <p className="eyebrow">QXRead · T3-E1 Checkout Preparation</p>
      <h2 id="route-title">Checkout</h2>
      <p>
        Checkout prepares deterministic totals from cart price snapshots. This step does not charge
        payment, mint entitlement, open Library, or launch Reader.
      </p>

      <dl className="publication-facts">
        <div>
          <dt>State</dt>
          <dd>{checkout.state}</dd>
        </div>
        <div>
          <dt>Service Fee</dt>
          <dd>{feePct}% buyer-side</dd>
        </div>
        <div>
          <dt>Affiliate / Referral</dt>
          <dd>
            {affPct}% / {refPct}% automatic contracts
          </dd>
        </div>
        {totals !== null ? (
          <>
            <div>
              <dt>Seller subtotal</dt>
              <dd>{formatTotal(totals.sellerSubtotalMinorUnits, totals.currency)}</dd>
            </div>
            <div>
              <dt>Service fee</dt>
              <dd>{formatTotal(totals.serviceFeeMinorUnits, totals.currency)}</dd>
            </div>
            <div>
              <dt>Buyer total</dt>
              <dd>{formatTotal(totals.buyerTotalMinorUnits, totals.currency)}</dd>
            </div>
            <div>
              <dt>Aff / Ref / RBS contracts</dt>
              <dd>
                {formatTotal(totals.affiliateContractMinorUnits, totals.currency)} /{' '}
                {formatTotal(totals.referralContractMinorUnits, totals.currency)} /{' '}
                {formatTotal(totals.rbsContractMinorUnits, totals.currency)}
              </dd>
            </div>
          </>
        ) : (
          <div>
            <dt>Blockers</dt>
            <dd>{checkout.blockers.join('; ') || 'none'}</dd>
          </div>
        )}
      </dl>

      <p className="route-panel__meta" role="status">
        Payment → Order → Entitlement · Library remains T4 · Reader remains T5 · Lines in cart:{' '}
        {cart.lines.length}
      </p>

      {checkout.state === 'ready' ? (
        <p>
          <button
            type="button"
            onClick={() => {
              const created = createDemoPendingOrder();
              pushLandingAlert(`Order ${created.orderId} created · pending payment`);
              setRevision((value) => value + 1);
              onNavigate('/orders');
            }}
          >
            Create pending order
          </button>
        </p>
      ) : null}

      {order !== null ? (
        <p role="status">
          Pending order {order.orderId} created · paymentAttemptId null · entitlementId null
        </p>
      ) : null}

      <p>
        <a
          href="#/cart"
          onClick={(event) => {
            event.preventDefault();
            onNavigate('/cart');
          }}
        >
          Back to cart
        </a>
        {' · '}
        <a
          href="#/orders"
          onClick={(event) => {
            event.preventDefault();
            onNavigate('/orders');
          }}
        >
          Orders
        </a>
      </p>
    </section>
  );
}
